import {
  IconContainer,
  LogSubtitle,
  LogTitle,
} from '../display/commonStyledComponents'
import { Form, FormCard } from '@lmc/ui-kit'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { incidentReportsConstants } from 'locale/incidentReports'
import useGoals from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoals'
import { useSelector } from 'react-redux'
import {
  Home,
  IncidentReport,
  IncidentSeverity,
  DetailsObject,
  FollowUp,
  FollowUpActionStatus,
} from '@lmc/ts-common'
import styled from 'styled-components'
import { FormInstance, Form as AntdForm } from 'antd'
import IncidentLogFormFields, {
  IncidentReportsLogFields,
} from './IncidentLogFormFields'
import IncidentCaseFormFields, {
  followUpActionsFieldName,
} from './IncidentCaseFormFields'
import { useEffect } from 'react'
import { ActionPlanValue } from 'screens/Residents/screens/ResidentsGoalsScreen/components/ActionPlanInput/ActionPlanInput'
import { AttachmentFieldType } from '../UploadAttachmentsOnSaveInput'
import useIncidentTypeOptions from 'hooks/useIncidentTypeOptions'

const INCIDENT_CATEGORY_ITEM = '5e867702b349f776c8d48c34'

type IncidentCaseFormProps = {
  form: FormInstance
  onFinish: (values: IncidentReportCaseForm<string>) => void
  onValuesChange: (
    values:
      | Partial<IncidentReportEditCaseForm<string>>
      | Partial<IncidentReportCreateCaseForm<string>>
  ) => void
  initialValues: Partial<IncidentReportCaseForm<string>>
  incidentReport: IncidentReport
}
const IncidentCaseForm = ({
  form,
  onFinish,
  onValuesChange,
  initialValues,
  incidentReport,
}: IncidentCaseFormProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const { goals } = useGoals(incidentReport?.serviceUser.id)
  const notificationRequired = AntdForm.useWatch(
    ['followUpActions', 'notificationRequired'],
    form
  )
  const safeguardingRequired = AntdForm.useWatch(
    ['followUpActions', 'safeguardingActionsRequired'],
    form
  )

  const incidentTypes = useIncidentTypeOptions(INCIDENT_CATEGORY_ITEM)

  const REQUIRES_REGULATOR_NOTIFICATION = t(
	incidentReportsConstants.REQUIRES_REGULATOR_NOTIFICATION
  )

  const REQUIRES_SAFEGUARDING = t(
	incidentReportsConstants.REQUIRES_SAFEGUARDING
  )
  const getActionsValue = (): ActionPlanValue<FollowUpActionStatus>[] =>
	form.getFieldValue(followUpActionsFieldName) || []

  const setActionsValue = (
	newValues: ActionPlanValue<FollowUpActionStatus>[]
  ) => form.setFieldValue(followUpActionsFieldName, newValues)

  const containsItemWithTitle = (title: string) =>
	Boolean(getActionsValue().find((value) => value.title === title))


  const regulatorNotificationActionInitialValue = initialValues.followUpActions?.actions.find(
	(action) => action.title === REQUIRES_REGULATOR_NOTIFICATION
  )

  const regulatorNotificationAction: ActionPlanValue<FollowUpActionStatus> = {
	title: REQUIRES_REGULATOR_NOTIFICATION,
	status: regulatorNotificationActionInitialValue?.status || FollowUpActionStatus.notStarted,
	id: undefined,
	canEdit: false,
	canDelete: false,
	assignedUserId: regulatorNotificationActionInitialValue?.assignedUserId || undefined,
  }
  useEffect(() => {

    if (notificationRequired) {
      if (!containsItemWithTitle(REQUIRES_REGULATOR_NOTIFICATION)) {
        setActionsValue([...getActionsValue(), regulatorNotificationAction])
      } else {
        const updatedValues = getActionsValue().map((value) =>
          value.title === REQUIRES_REGULATOR_NOTIFICATION
            ? regulatorNotificationAction
            : value
        )
        setActionsValue(updatedValues)
      }
    } else if (containsItemWithTitle(REQUIRES_REGULATOR_NOTIFICATION)) {
      const filteredValues = getActionsValue()?.filter(
        (value) => value.title !== REQUIRES_REGULATOR_NOTIFICATION
      )
      setActionsValue(filteredValues)
    }
  }, [notificationRequired])


  const requiredSafeguaringInitialValue = initialValues.followUpActions?.actions.find(
	(action) => action.title === REQUIRES_SAFEGUARDING
  )
  const safeguardingAction: ActionPlanValue<FollowUpActionStatus> = {
	title: REQUIRES_SAFEGUARDING,
	status: requiredSafeguaringInitialValue?.status || FollowUpActionStatus.notStarted,
	id: undefined,
	canEdit: false,
	canDelete: false,
	assignedUserId: requiredSafeguaringInitialValue?.assignedUserId || undefined,
  }
  useEffect(() => {

    if (safeguardingRequired) {
      if (!containsItemWithTitle(REQUIRES_SAFEGUARDING)) {
        setActionsValue([...getActionsValue(), safeguardingAction])
      } else {
        const updatedValues = getActionsValue().map((value) =>
          value.title === REQUIRES_SAFEGUARDING ? safeguardingAction : value
        )
        setActionsValue(updatedValues)
      }
    } else if (containsItemWithTitle(REQUIRES_SAFEGUARDING)) {
      const filteredValues = getActionsValue()?.filter(
        (value) => value.title !== REQUIRES_SAFEGUARDING
      )
      setActionsValue(filteredValues)
    }
  }, [safeguardingRequired])


  const handleValuesChange = (
    changedValues: Partial<IncidentReportCaseForm<string>>
  ) => {
    onValuesChange(changedValues)
  }

  return (
    <Form<IncidentReportCaseForm<string>>
      form={form}
      onFinish={onFinish}
      onValuesChange={handleValuesChange}
      initialValues={initialValues}
      onFinishFailed={({ errorFields }) =>
        errorFields.map((errorField) =>
          errorField.errors.map((err) =>
            showToast({ title: err, type: 'error' })
          )
        )
      }
    >
      <FormCard
        title={
          <RowContainer>
            <IconContainer
              src={
                (incidentReport.log.item?.icon ||
                  incidentReport.log.category?.icon) as unknown as string
              }
              $color={incidentReport.log.category.color}
              alt="icon"
            />
            <ColumnContainer>
              <LogTitle level={3}>{incidentReport.log.title}</LogTitle>
              <LogSubtitle>
                {t(incidentReportsConstants.LOGGED_BY)}{' '}
                {incidentReport.log.loggedBy?.name || 'carer'}
              </LogSubtitle>
            </ColumnContainer>
          </RowContainer>
        }
      >
        <IncidentLogFormFields
          isCase
          goals={goals}
          locations={home.locations}
          incidentTypes={incidentTypes}
          // @ts-ignore
          hasLocationsEnabled={home?.features?.hasHomeLocations?.enabled}
        />
      </FormCard>
      <IncidentCaseFormFields
        form={form}
        severities={home.incidentSeverity}
        residentId={incidentReport.serviceUser.id}
      />
    </Form>
  )
}

const RowContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`

// fields included in both create/edit forms
export type IncidentReportCaseFormBase<T> = {
  serviceUsersInvolved: string[]
  // here you need to add the type from LogForm
  log: IncidentReportsLogFields<T>
  severity: null | IncidentSeverity
  otherPeopleInvolved: null | string
  physicalIntervention: DetailsObject
  restraints: DetailsObject
  chemicalRestraints: DetailsObject
  residentInjuries: DetailsObject
  carersInjuries: DetailsObject
  otherPartiesInjuries: DetailsObject
  damages: DetailsObject
  prnMedication: DetailsObject
  otherClientsInvolved: [] | null
  otherWitnesses: string | null
  serviceUserOpinion: string | null
  incidentOtherInformation: string | null
  followUpActions: FollowUp
  debriefingNotes: string | null
  rootCause: string | null
  preventativeActions: string | null
  otherNotes: string | null
  attachments: AttachmentFieldType[]
  carersInvolved: string[]
}

// fields only for edit case
export interface IncidentReportEditCaseForm<T>
  extends IncidentReportCaseFormBase<T> {}

// fields only for create case
export interface IncidentReportCreateCaseForm<T>
  extends IncidentReportCaseFormBase<T> {}

// both edit/ create
export type IncidentReportCaseForm<T> =
  | IncidentReportEditCaseForm<T>
  | IncidentReportCreateCaseForm<T>

export default IncidentCaseForm
